import { appStorage, appSession } from '../../lib/safeStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, User, Building2, Clock, X, MessageSquare, AlertTriangle, Zap, DollarSign, Calendar, Menu, Music, PartyPopper, Star, Settings2, ArrowLeft, VolumeX, Volume2, LogOut, HelpCircle, Eye, CheckCheck, Send, Edit3, UserPlus, Users, Siren, ShieldAlert, Info, CheckCircle, Shield, RefreshCw, Megaphone, AlarmClock, Sparkles, ChevronDown, Search, Phone, Mail } from 'lucide-react';
import Sidebar from './Sidebar';
import RoleSwitcher from '../RoleSwitcher';
import NotificationPrompt from '../NotificationPrompt';
import BlockoutAlertBanner from '../BlockoutAlertBanner';
import StaffDeclineAlertBanner from '../StaffDeclineAlertBanner';
import StaffBottomNav from './StaffBottomNav';
import { cn } from '../../lib/utils';
import { notificationsAPI, companyAPI } from '../../services/api';
import useDismissable from '../../hooks/useDismissable';
import { useBadges } from '../../context/NotificationProvider';
import { setTenantTimezone } from '../../lib/datetime';
import { toast as sonnerToast } from 'sonner';
import RolePreviewSwitcher from '../RolePreviewSwitcher';
import useLuminateOnEvent from '../../hooks/useLuminateOnEvent';
import usePolling from '../../hooks/usePolling';
import NeedsAttentionStack from './NeedsAttentionStack';
import { playNewLeadChime } from '../../lib/audioChime';
import { pageTitles } from './pageTitles';
import apiClient from '../../services/apiClient';
import StaffedProCertificate from '../StaffedProCertificate';
import HelpMenu from '../help/HelpMenu';

// Page title configurations live in ./pageTitles (imported above for shared use
// with PageHeader). Layout uses this map only for the sticky topbar mini-title;
// each page renders its own in-page H1 via <PageHeader />.

// Preference category config
const PREF_CATEGORIES = [
  { key: 'shift_assigned', icon: Calendar, label: 'Assignments' },
  { key: 'shift_unassigned', icon: Calendar, label: 'Unassignments' },
  { key: 'announcement', icon: Zap, label: 'Team Announcements' },
  { key: 'heat_points', icon: Zap, label: 'Heat Points' },
  { key: 'chat', icon: MessageSquare, label: 'Chat Messages' },
  { key: 'shift_reminder', icon: Calendar, label: 'Event Reminders' },
  { key: 'missing_pay', icon: AlertTriangle, label: 'Missing Pay Alerts' },
  { key: 'payment', icon: DollarSign, label: 'Payments' },
];

const MiniToggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex items-center flex-shrink-0 h-7 w-12 rounded-full transition-colors duration-200 ease-out ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'bg-[#D4AF37]' : 'bg-white/20'}`}
    style={checked ? { boxShadow: '0 0 0 1px rgba(212,175,55,0.45), 0 2px 10px -2px rgba(212,175,55,0.5)' } : undefined}
    data-testid="preference-toggle"
  >
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.4)] transform transition-transform duration-200 ease-out ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const DashboardLayout = ({ children, user, realUser, onLogout, pendingRatingsCount = 0, onShowRatings }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(user?.companyLogo || null);
  const [logoBackground, setLogoBackground] = useState(user?.logoBackground || 'dark');
  // Office contact (from company settings) for the staff/team "Contact Office"
  // shortcut in the profile dropdown. Settings are owner-scoped, so a staff
  // member's fetch returns THEIR office's phone/email.
  const [officePhone, setOfficePhone] = useState(null);
  const [officeEmail, setOfficeEmail] = useState(null);
  // Pass-through fee reminder banner
  const [feePassThrough, setFeePassThrough] = useState(null); // null = unknown
  const [enablingFee, setEnablingFee] = useState(false);
  const [feeBannerDismissed, setFeeBannerDismissed] = useState(
    () => (typeof window !== 'undefined' && appSession.getItem('feeBannerDismissed') === '1')
  );
  // Turn on fee pass-through IN PLACE (no trip to the settings page). Reads the
  // current fee settings and re-saves with passThrough=true so nothing else
  // (percentages, Zelle/check config) is disturbed.
  const enablePassThroughInline = useCallback(async () => {
    setEnablingFee(true);
    try {
      const { data } = await apiClient.get('/payments/fee-settings');
      await apiClient.put('/payments/fee-settings', {
        zelleEnabled: !!data.zelleEnabled,
        zelleHandle: data.zelleHandle || '',
        zelleName: data.zelleName || '',
        checkEnabled: !!data.checkEnabled,
        checkPayableTo: data.checkPayableTo || '',
        checkAddress: data.checkAddress || '',
        externalLinkEnabled: !!data.externalLinkEnabled,
        externalLinkLabel: data.externalLinkLabel || '',
        externalLinkUrl: data.externalLinkUrl || '',
        externalLinkInstructions: data.externalLinkInstructions || '',
        percentageFee: Number(data.percentageFee ?? 0.029),
        fixedFee: Number(data.fixedFee ?? 0.30),
        passThrough: true,
        feeLabel: data.feeLabel || 'Processing Fee',
        defaultDepositPct: (Number.isInteger(data.defaultDepositPct) && data.defaultDepositPct >= 1 && data.defaultDepositPct <= 100) ? data.defaultDepositPct : 50,
      });
      setFeePassThrough(true);
      window.dispatchEvent(new CustomEvent('feeSettingsUpdated', { detail: { passThrough: true } }));
      sonnerToast.success('Pass-through on — clients now cover the processing fee.');
    } catch (e) {
      sonnerToast.error('Could not turn it on — try again.');
    } finally {
      setEnablingFee(false);
    }
  }, []);
  const [showNotifications, setShowNotifications] = useState(false);
  // Lock the page behind the mobile notification sheet so only the sheet scrolls (Mo 2026-07).
  useEffect(() => {
    if (!showNotifications) return;
    const _y = window.scrollY;
    const _b = document.body.style;
    const _prev = { position: _b.position, top: _b.top, width: _b.width, overflow: _b.overflow };
    _b.position = 'fixed';
    _b.top = `-${_y}px`;
    _b.width = '100%';
    _b.overflow = 'hidden';
    return () => {
      _b.position = _prev.position;
      _b.top = _prev.top;
      _b.width = _prev.width;
      _b.overflow = _prev.overflow;
      window.scrollTo(0, _y);
    };
  }, [showNotifications]);
  // Swipe-down-to-close for the mobile Notifications bottom-sheet.
  const notifSheetDrag = useRef({ tracking: false, active: false, kind: 'handle', listEl: null, startY: 0, lastY: 0, lastT: 0, v: 0 });
  const [notifDragY, setNotifDragY] = useState(0);
  const [notifSheetDragging, setNotifSheetDragging] = useState(false);
  const [notifInteracted, setNotifInteracted] = useState(false);
  const notifReducedMotion = useRef(typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function endNotifTracking() {
    window.removeEventListener('pointermove', onNotifSheetMove);
    window.removeEventListener('pointerup', onNotifSheetEnd);
    window.removeEventListener('pointercancel', onNotifSheetEnd);
    notifSheetDrag.current.tracking = false;
    notifSheetDrag.current.active = false;
  }

  function onNotifSheetMove(e) {
    const d = notifSheetDrag.current;
    if (!d.tracking) return;
    const y = e.clientY;
    const dy = y - d.startY;
    if (!d.active) {
      const atTop = d.listEl ? d.listEl.scrollTop <= 0 : true;
      if (dy > 5 && (d.kind === 'header' || (d.kind === 'list' && atTop))) {
        d.active = true;
        setNotifSheetDragging(true);
        setNotifInteracted(true);
      } else if (dy < -2 || (d.kind === 'list' && !atTop)) {
        endNotifTracking();
        return;
      } else {
        return;
      }
    }
    const now = performance.now();
    const dt = now - d.lastT;
    if (dt > 0) d.v = (y - d.lastY) / dt;
    d.lastY = y;
    d.lastT = now;
    if (!notifReducedMotion.current) setNotifDragY(Math.max(0, dy));
    if (e.cancelable) e.preventDefault();
  }

  function onNotifSheetEnd() {
    const d = notifSheetDrag.current;
    const wasActive = d.active;
    const dy = Math.max(0, d.lastY - d.startY);
    const v = d.v;
    endNotifTracking();
    setNotifSheetDragging(false);
    if (!wasActive) return;
    const shouldClose = dy > 90 || v > 0.5;
    if (shouldClose) {
      if (notifReducedMotion.current) { setNotifDragY(0); setShowNotifications(false); return; }
      setNotifDragY(window.innerHeight);
      setTimeout(() => { setNotifDragY(0); setShowNotifications(false); }, 280);
    } else {
      setNotifDragY(0);
    }
  }

  function beginNotifDrag(e, kind, listEl) {
    const d = notifSheetDrag.current;
    d.tracking = true;
    d.active = kind === 'handle';
    d.kind = kind;
    d.listEl = listEl || null;
    d.startY = e.clientY;
    d.lastY = e.clientY;
    d.lastT = performance.now();
    d.v = 0;
    if (d.active) setNotifSheetDragging(true);
    if (d.active) setNotifInteracted(true);
    window.addEventListener('pointermove', onNotifSheetMove, { passive: false });
    window.addEventListener('pointerup', onNotifSheetEnd);
    window.addEventListener('pointercancel', onNotifSheetEnd);
  }

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // S72 — close the profile dropdown on outside click / touch / Escape via the
  // ONE shared behaviour. Document-level (stacking-context agnostic): the
  // header's backdrop-filter would scope a fixed backdrop and miss clicks
  // outside the header.
  useDismissable(profileMenuRef, showProfileMenu, useCallback(() => setShowProfileMenu(false), []));
  const [notifView, setNotifView] = useState('list'); // 'list' | 'preferences'
  const [notifFilter, setNotifFilter] = useState('all'); // 'all' | 'unread'
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [ratingFlash, setRatingFlash] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const prefsRef = useRef(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [expandedCascades, setExpandedCascades] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Reset scroll to top on every route change so a new page never opens
  // mid-scroll — a major source of the "jerky" feel when navigating from a
  // scrolled-down page. Instant (no smooth) so it doesn't fight the fade.
  // EXCEPTION: switching tabs *within* /settings must preserve scroll position
  // (the settings panes scroll independently), so skip the snap-to-top when
  // both the previous and next route are under /settings.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;
    const bothSettings = prev.startsWith('/settings') && next.startsWith('/settings');
    if (bothSettings) return;
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (_) { window.scrollTo(0, 0); }
  }, [location.pathname]);

  // Flash the bell every 15 minutes when there are pending ratings
  useEffect(() => {
    if (pendingRatingsCount <= 0) {
      setRatingFlash(false);
      return;
    }
    // Flash immediately when pending ratings appear
    setRatingFlash(true);
    const hideTimeout = setTimeout(() => setRatingFlash(false), 8000);
    // Then flash again every 15 minutes
    const interval = setInterval(() => {
      setRatingFlash(true);
      setTimeout(() => setRatingFlash(false), 8000);
    }, 15 * 60 * 1000);
    return () => { clearTimeout(hideTimeout); clearInterval(interval); };
  }, [pendingRatingsCount]);

  // Fetch fresh company logo + tenant timezone from API. Stale appStorage
  // cannot be trusted for either — the tenant zone in particular controls
  // every date/time render across the app, so we set it as early as
  // possible after mount.
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const settings = await companyAPI.getSettings();
        if (settings?.logo) {
          setCompanyLogo(settings.logo);
        }
        if (settings?.logoBackground) {
          setLogoBackground(settings.logoBackground);
        }
        if (settings?.timezone) {
          setTenantTimezone(settings.timezone);
        }
        if (settings?.phone) setOfficePhone(settings.phone);
        if (settings?.email) setOfficeEmail(settings.email);
      } catch {
        // silent — keep user prop fallback
      }
    };
    fetchLogo();
  }, []);

  // Fetch notifications from backend with LIVE toast/luminate dispatch.
  // Each poll diffs the result against a seen-set to detect newly-arrived
  // unread items. Client-question notifications fire a Sonner toast +
  // dispatch a `mtl:client-message-new` window event so the workspace
  // chat card can luminate. The seen-set is seeded silently on the first
  // poll so we never toast stale notifications on initial load.
  const seenIdsRef = useRef(new Set());
  const firstNotificationFetchRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const data = await notificationsAPI.getAll(20);
      const items = Array.isArray(data) ? data : data.notifications || [];
      setNotifications(items);

      // Diff for newly-arrived unread items
      if (firstNotificationFetchRef.current) {
        items.forEach(n => seenIdsRef.current.add(n.id));
        firstNotificationFetchRef.current = false;
        return;
      }
      const fresh = items.filter(n => !seenIdsRef.current.has(n.id) && !n.read);
      for (const n of fresh) {
        seenIdsRef.current.add(n.id);
        const meta = n.metadata || {};
        const type = n.type || n.notification_type || '';
        const eventId = n.related_id || meta.eventId || meta.event_id;
        // S15 Item 1b — muted / category-off suppresses the ALERTS only (web
        // push, this auto-toast, and the sound). The bell row still arrives and
        // the luminate/refresh signals below still fire. `alertsSuppressed` is
        // computed server-side so all three channels obey one rule.
        const silent = !!n.alertsSuppressed;

        // Map notification type → typed window event + toast tone so cards
        // across the app can luminate / pulse for the signal they care about.
        const TYPE_TO_EVENT = {
          client_question:           'mtl:client-message-new',
          payment:                   'mtl:payment-received',
          payment_schedule_created:  'mtl:payment-received',
          zelle_payment_sent:        'mtl:payment-received',
          check_payment_mailed:      'mtl:payment-received',
          card_payment_completed:    'mtl:payment-received',
          new_lead:                  'mtl:lead-new',
          proposal_viewed:           'mtl:proposal-viewed',
          proposal_approved:         'mtl:proposal-approved',
          planning_submitted:        'mtl:planning-submitted',
          staffing_gap:              'mtl:staffing-update',
          shift_reminder:            'mtl:staffing-update',
          live_issue:                'mtl:live-issue',
          event_complete:            'mtl:event-complete',
        };
        const evtName = TYPE_TO_EVENT[type] || 'mtl:notification-new';
        try {
          window.dispatchEvent(new CustomEvent(evtName, { detail: { eventId, notification: n } }));
          // Always also dispatch the generic event so a global "any new
          // notification" listener (e.g. ambient ping sound) can subscribe.
          if (evtName !== 'mtl:notification-new') {
            window.dispatchEvent(new CustomEvent('mtl:notification-new', { detail: n }));
          }
        } catch { /* SSR-safe */ }

        // Staff "New Assignment Sound" — chime when the staffer is newly added
        // to an event (notification_type "assignment"). Respects the
        // assignmentSound preference (default ON) + global mute. Mirrors the
        // admin new-lead chime; only staff ever receive this type.
        if (type === 'assignment') {
          const cp = prefsRef.current;
          if (!silent && cp?.assignmentSound !== false && !cp?.muteAll) {
            playNewLeadChime();
          }
        }

        // Sonner toast — choose severity + action by category.
        // Notifications carry a backend `link`, but event links usually arrive
        // as a bare `/event/{id}` with no `?section=` — which drops the admin on
        // the default Proposal tab instead of the tab the toast is about. Map
        // the type to its workspace module and append the matching section onto
        // bare event links (links that already target a section, or non-event
        // links like /staff-portal or /events/budgets, are left untouched).
        const TYPE_TO_SECTION = {
          payment: 'payments',
          payment_schedule_created: 'payments',
          zelle_payment_sent: 'payments',
          check_payment_mailed: 'payments',
          card_payment_completed: 'payments',
          proposal_viewed: 'proposal',
          proposal_approved: 'proposal',
          planning_submitted: 'planning',
          staffing_gap: 'staffing',
          shift_reminder: 'staffing',
          live_issue: 'control',
        };
        const wantSection = TYPE_TO_SECTION[type] || (type.includes('payment') ? 'payments' : '');
        let link = n.link || (eventId ? `/event/${eventId}` : null);
        if (link && wantSection && /\/event\/[^?]+$/.test(link)) {
          link = `${link}?section=${wantSection}`;
        }
        const action = link ? { label: 'Open', onClick: () => navigate(link) } : undefined;
        if (!silent) {
        if (type === 'client_question') {
          sonnerToast.message(n.title || 'New message from client', {
            description: n.message || '',
            action: link ? { label: 'Open chat', onClick: () => navigate(`/event/${eventId}?section=messages`) } : undefined,
            duration: 5000,
          });
        } else if (type === 'live_issue' || type === 'blocker' || type === 'staffing_gap') {
          sonnerToast.error(n.title || 'Heads-up', { description: n.message || '', action, duration: 5000 });
        } else if (type === 'payment' || type.includes('payment')) {
          sonnerToast.success(n.title || 'Payment update', { description: n.message || '', action, duration: 5000 });
        } else if (type === 'new_lead') {
          // New leads are higher-priority signal — bigger toast, stays
          // on screen ~50% longer, gold accent + a clear icon so it's
          // impossible to miss when admins are heads-down on other tabs.
          // Still uses the existing sonner toast system — no new banner
          // component or notification path.

          // Audio ping — two-note ascending chime. Respects the user's
          // "New Lead Sound" preference (default ON) and the global
          // mute-all switch. Falls back silently if the browser blocks
          // autoplay before any user interaction.
          const currentPrefs = prefsRef.current;
          const soundEnabled = currentPrefs?.leadSound !== false;
          const muted = !!currentPrefs?.muteAll;
          if (soundEnabled && !muted) {
            playNewLeadChime();
          }

          // S44: unified toast shell — no custom band/style/icon that fights
          // the shared look. Keeps the trigger, text and action unchanged; the
          // lead chime above is untouched.
          sonnerToast.success(n.title || 'New lead captured', {
            description: n.message || '',
            action: link ? { label: 'View lead', onClick: () => navigate(link) } : undefined,
          });
        } else if (type === 'proposal_viewed' || type === 'proposal_approved' || type === 'planning_submitted') {
          sonnerToast.success(n.title || 'Update', { description: n.message || '', action, duration: 5000 });
        } else if (type === 'checkin') {
          // In-range GPS check-in — positive/success tone (green + check),
          // distinct from the error-styled out-of-range exception (checkin_alert).
          sonnerToast.success(n.title || 'Checked in', { description: n.message || '', action, duration: 5000, icon: <CheckCircle className="w-4 h-4 text-emerald-400" /> });
        } else {
          sonnerToast.message(n.title || 'New notification', { description: n.message || '', action, duration: 5000 });
        }
        } // end if (!silent) — alerts suppressed by prefs (S15 Item 1b)
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [navigate]);

  // Load notifications on mount + subscribe to the shared NotificationProvider
  // stream. The dedicated 12s poll here was killed — every badge and the bell
  // list now poll ONCE (5s) via NotificationProvider. This effect just mirrors
  // the provider's notifications into local state so the existing bell-dropdown
  // JSX and toast-diff logic stay untouched.
  const { notifications: providerNotifications, markBellRead, badges: liveBadges, ackCategory } = useBadges();
  useEffect(() => {
    if (!Array.isArray(providerNotifications)) return;
    setNotifications(providerNotifications);
    // Run the toast-diff logic against the provider's items so brand-new
    // unread notifications continue to emit their typed window events.
    fetchNotifications._fromProvider = true;
    // Diff for newly-arrived unread items — mirrors the original inline logic.
    if (firstNotificationFetchRef.current) {
      providerNotifications.forEach((n) => seenIdsRef.current.add(n.id));
      firstNotificationFetchRef.current = false;
      return;
    }
    const fresh = providerNotifications.filter((n) => !seenIdsRef.current.has(n.id) && !n.read);
    for (const n of fresh) {
      seenIdsRef.current.add(n.id);
      // Emit the same toast/window events fetchNotifications used to fire.
      // Delegating to fetchNotifications avoids duplicating the mapping table.
    }
    // Fall-through: call fetchNotifications only if there's a genuinely new
    // item to run its emit path; otherwise skip the network trip.
    if (fresh.length > 0) {
      fetchNotifications().catch(() => {});
    }
  }, [providerNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      await notificationsAPI.markAsRead(notification.id);
      setNotifications(notifications.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
    setShowNotifications(false);
    // Any role can tap a notification to jump to the linked item; the links
    // point at role-appropriate routes (staff -> /my-events, etc.).
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Mark all as read
  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Clear all — destructive, so the first press only opens the inline confirm
  // strip below. Native window.confirm is banned; it freezes the panel.
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const clearAll = async () => {
    if (clearingAll) return;
    setClearingAll(true);
    try {
      await notificationsAPI.clearAll();
      // unreadCount is derived from `notifications` (line ~729), so emptying
      // the list zeroes the badge — there is no separate counter to reset.
      setNotifications([]);
      setClearAllConfirm(false);
    } catch (error) {
      sonnerToast.error(error?.response?.data?.detail || 'Failed to clear notifications');
    } finally {
      setClearingAll(false);
    }
  };

  // ── Cascade grouping ─────────────────────────────────────────────────
  // Notifications that share metadata.cascadeId (e.g. the 4 fired when a
  // contract is signed — event/client/location/budget) collapse into one
  // expandable group header so the inbox doesn't feel noisy.
  const buildGroupedFeed = (list) => {
    const seen = new Set();
    const feed = [];
    for (const n of list) {
      const cid = n?.metadata?.cascadeId;
      if (!cid) { feed.push({ kind: 'single', notification: n }); continue; }
      if (seen.has(cid)) continue;
      seen.add(cid);
      const members = list.filter(m => m?.metadata?.cascadeId === cid);
      if (members.length < 2) feed.push({ kind: 'single', notification: n });
      else feed.push({ kind: 'group', cascadeId: cid, members });
    }
    return feed;
  };

  const toggleCascade = (cid) => {
    setExpandedCascades(prev => ({ ...prev, [cid]: !prev[cid] }));
  };

  const handleGroupPrimaryClick = async (group) => {
    const primary = group.members.find(m => m?.metadata?.cascadeStep === 'event') || group.members[0];
    const unread = group.members.filter(m => !m.read);
    try {
      await Promise.all(unread.map(m => notificationsAPI.markAsRead(m.id)));
      setNotifications(prev => prev.map(n =>
        group.members.some(m => m.id === n.id) ? { ...n, read: true } : n
      ));
    } catch (err) {
      console.error('Failed to mark cascade read:', err);
    }
    setShowNotifications(false);
    const userRole = user?.role || '';
    const isStaffOrVendor = userRole === 'staff' || userRole === 'vendor';
    if (primary?.link && !isStaffOrVendor) navigate(primary.link);
  };

  // Fetch notification preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const data = await notificationsAPI.getPreferences();
      setPrefs(data);
      prefsRef.current = data;
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    }
  }, []);

  // Fetch preferences on mount so the new-lead chime knows whether the
  // user has the sound toggle disabled. (Otherwise we'd only know after
  // they open the preferences panel.)
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggleCategory = async (key, value) => {
    if (!prefs) return;
    setSavingPrefs(true);
    try {
      const result = await notificationsAPI.updatePreferences({ categories: { ...prefs.categories, [key]: value } });
      setPrefs(result);
      prefsRef.current = result;
    } catch (err) {
      console.error('Error updating preferences:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleToggleMuteAll = async (value) => {
    setSavingPrefs(true);
    try {
      const result = await notificationsAPI.updatePreferences({ muteAll: value });
      setPrefs(result);
      prefsRef.current = result;
    } catch (err) {
      console.error('Error updating mute:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleToggleLeadSound = async (value) => {
    setSavingPrefs(true);
    try {
      const result = await notificationsAPI.updatePreferences({ leadSound: value });
      setPrefs(result);
      prefsRef.current = result;
      // Preview the chime so the user can hear what they just enabled.
      if (value) playNewLeadChime();
    } catch (err) {
      console.error('Error updating lead sound:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleToggleAssignmentSound = async (value) => {
    setSavingPrefs(true);
    try {
      const result = await notificationsAPI.updatePreferences({ assignmentSound: value });
      setPrefs(result);
      prefsRef.current = result;
      // Preview the chime so staff hear what they just enabled.
      if (value) playNewLeadChime();
    } catch (err) {
      console.error('Error updating assignment sound:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Load preferences when switching to preferences view
  useEffect(() => {
    if (showNotifications && notifView === 'preferences') {
      fetchPreferences();
    }
  }, [showNotifications, notifView, fetchPreferences]);

  // Reset view when closing
  useEffect(() => {
    if (!showNotifications) setNotifView('list');
    if (!showNotifications) setClearAllConfirm(false);
    if (!showNotifications) { setNotifInteracted(false); setNotifDragY(0); setNotifSheetDragging(false); }
  }, [showNotifications]);
  // Opening the notifications = "checked" → optimistic clear via provider
  // (zeros bell immediately + marks everything read on the server). No wait
  // for the next 5s poll.
  useEffect(() => {
    if (!showNotifications) return;
    if (!notifications.some((n) => !n.read)) return;
    markBellRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [showNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // S135 — opening the bell also marks NEW-LEAD alerts seen so the lead-only
  // bell pulse clears immediately and stays cleared after reload. Reuses the
  // existing ack path (ackCategory → notification_acks); adds no new source.
  useEffect(() => {
    if (!showNotifications) return;
    if ((liveBadges?.leads ?? 0) > 0) ackCategory('leads');
    if ((liveBadges?.live_leads ?? 0) > 0) ackCategory('live_leads');
  }, [showNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get notification icon based on type
  const NOTIF_STYLE = {
    proposal_viewed:    { Icon: Eye,          color: 'text-blue-400',      bg: 'bg-blue-500/15' },
    planning_submitted: { Icon: CheckCheck,   color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    new_lead:           { Icon: UserPlus,     color: 'text-purple-400',    bg: 'bg-purple-500/15' },
    pipeline:           { Icon: Info,         color: 'text-[#D4AF37]',     bg: 'bg-[#D4AF37]/15' },
    blocker:            { Icon: ShieldAlert,  color: 'text-red-400',       bg: 'bg-red-500/15' },
    staffing_gap:       { Icon: Users,        color: 'text-orange-400',    bg: 'bg-orange-500/15' },
    live_issue:         { Icon: Siren,        color: 'text-red-400',       bg: 'bg-red-500/15' },
    staff_response:     { Icon: Users,        color: 'text-blue-400',      bg: 'bg-blue-500/15' },
    booking_conversion: { Icon: DollarSign,   color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    event_complete:     { Icon: CheckCircle,  color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    checkin:            { Icon: CheckCircle,  color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    missing_pay:        { Icon: AlertTriangle,color: 'text-red-400',       bg: 'bg-red-500/15' },
    announcement:       { Icon: Megaphone,    color: 'text-purple-400',    bg: 'bg-purple-500/15' },
    chat:               { Icon: MessageSquare,color: 'text-sky-400',       bg: 'bg-sky-500/15' },
    heat:               { Icon: Zap,          color: 'text-orange-400',    bg: 'bg-orange-500/15' },
    heat_points:        { Icon: Zap,          color: 'text-orange-400',    bg: 'bg-orange-500/15' },
    shift_assigned:     { Icon: UserPlus,     color: 'text-blue-400',      bg: 'bg-blue-500/15' },
    shift_unassigned:   { Icon: Users,        color: 'text-red-400',       bg: 'bg-red-500/15' },
    event_reminder:     { Icon: Calendar,     color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    shift_reminder:     { Icon: Calendar,     color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    payment:            { Icon: DollarSign,   color: 'text-[#D4AF37]',     bg: 'bg-[#D4AF37]/15' },
    followup_reminder:  { Icon: AlarmClock,   color: 'text-amber-400',     bg: 'bg-amber-500/15' },
    event_feedback:     { Icon: MessageSquare,color: 'text-emerald-400',   bg: 'bg-emerald-500/15' },
    referral:           { Icon: UserPlus,     color: 'text-[#D4AF37]',     bg: 'bg-[#D4AF37]/15' },
  };

  const getNotifStyle = (notification) => {
    const type = notification.type;
    if (type === 'pipeline') {
      const action = notification.metadata?.action || notification.title || '';
      if (action.includes('revision') || action.includes('Revision')) return { Icon: Edit3, color: 'text-orange-400', bg: 'bg-orange-500/15' };
      if (action.includes('proposal_sent') || action.includes('Proposal Sent') || action.includes('Proposal Out')) return { Icon: Send, color: 'text-gray-400', bg: 'bg-white/8' };
      if (action.includes('contract_sent') || action.includes('Contract Sent') || action.includes('Contract Out')) return { Icon: Send, color: 'text-blue-400', bg: 'bg-blue-500/15' };
      if (action.includes('proposal_approved') || action.includes('Proposal Approved')) return { Icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/15' };
      if (action.includes('contract_signed') || action.includes('Contract Signed')) return { Icon: CheckCircle, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/15' };
      if (action.includes('restored') || action.includes('Restored')) return { Icon: RefreshCw, color: 'text-emerald-400', bg: 'bg-emerald-500/15' };
      if (action.includes('archived') || action.includes('Archived')) return { Icon: Shield, color: 'text-gray-400', bg: 'bg-white/8' };
    }
    return NOTIF_STYLE[type] || { Icon: Info, color: 'text-gray-400', bg: 'bg-white/8' };
  };

  // Legacy icon helper (for mobile panel compatibility)
  const getNotificationIcon = (type) => {
    const s = NOTIF_STYLE[type] || { Icon: Info, color: 'text-gray-400' };
    const I = s.Icon;
    return <I className={`w-4 h-4 ${s.color}`} />;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // S134 — authoritative UNSEEN signal. ONE source of truth: badges.bell from
  // the single NotificationProvider /api/badges poll (server counts unread,
  // uncleared rows scoped to this user/tenant). Both intake leads
  // (notify_new_lead) and live leads (dispatch_notification) write exactly ONE
  // read:false row → +1 here, no second detection. markBellRead() zeros it
  // optimistically on open, so the badge number AND the gold pulse clear
  // instantly and reopening shows no phantom "new".
  const bellUnseen = Math.max(0, liveBadges?.bell ?? 0);

  // S135 — the PULSE is lead-only. It is driven SOLELY by the unseen-LEAD
  // signal (ack-based leads + live_leads badge counts from the same single
  // /api/badges poll), NOT by the global unread bell count. So 4 unread
  // blockers with leads:0/live_leads:0 must NOT pulse. Clears when the admin
  // sees the leads: opening the bell acks both lead categories (effect below)
  // or visiting the leads page (existing sidebar clear-on-view ack). Both
  // persist server-side via notification_acks, so no phantom pulse on reload.
  const leadUnseen = Math.max(0, (liveBadges?.leads ?? 0) + (liveBadges?.live_leads ?? 0));

  // Bell-level ambient luminate: pulse gold any time a generic notification
  // signal lands. Works independently of category-specific cards so users
  // always see the chrome react first when something arrives.
  const bellLuminate = useLuminateOnEvent('mtl:notification-new');

  // Listen for company settings updates to refresh the logo
  useEffect(() => {
    const handleCompanyUpdate = (event) => {
      const { companyLogo: newLogo, logoBackground: newBg } = event.detail;
      if (newLogo !== undefined) setCompanyLogo(newLogo);
      if (newBg !== undefined) setLogoBackground(newBg);
    };

    window.addEventListener('companySettingsUpdated', handleCompanyUpdate);
    return () => window.removeEventListener('companySettingsUpdated', handleCompanyUpdate);
  }, []);

  // Update from user prop changes
  useEffect(() => {
    if (user?.companyLogo) setCompanyLogo(user.companyLogo);
    if (user?.logoBackground) setLogoBackground(user.logoBackground);
  }, [user?.companyLogo, user?.logoBackground]);

  useEffect(() => {
    const isAdmin = user?.role !== 'staff' && user?.role !== 'vendor' && user?.role !== 'member';
    if (!isAdmin) return undefined;
    let alive = true;
    apiClient.get('/payments/fee-settings')
      .then(({ data }) => { if (alive) setFeePassThrough(!!data?.passThrough); })
      .catch(() => {});
    const onFeeUpdate = (e) => setFeePassThrough(!!e.detail?.passThrough);
    window.addEventListener('feeSettingsUpdated', onFeeUpdate);
    return () => { alive = false; window.removeEventListener('feeSettingsUpdated', onFeeUpdate); };
  }, [user?.role]);

  // Get page title based on current route
  const getPageTitle = () => {
    // /dashboard now redirects to /control-center, but handle gracefully
    if (location.pathname === '/dashboard') {
      return { title: 'Command Deck', subtitle: 'Monitor and manage all your events' };
    }

    // Check for exact match first
    if (pageTitles[location.pathname]) {
      return pageTitles[location.pathname];
    }
    
    // Tier 5B Pass 3: the cross-event "Command Deck" lives at
    // `/control-center`. The per-event view is now reached as a
    // workspace tab and titled "Event Control" — but if a legacy
    // URL (`/events/:id/control-center`) is hit before its redirect,
    // fall through to the cross-event title since that's where the
    // user lands.
    if (location.pathname.includes('/control-center')) {
      return { title: 'Command Deck', subtitle: 'Monitor and manage all your events' };
    }
    
    // Check for partial matches (for nested routes)
    for (const [path, config] of Object.entries(pageTitles)) {
      if (location.pathname.startsWith(path) && path !== '/') {
        return config;
      }
    }
    
    // Default
    return { title: 'Command Deck', subtitle: '' };
  };

  const { title, subtitle } = getPageTitle();

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar 
        user={user} 
        onLogout={onLogout}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <div 
        className={cn(
          "transition-[margin] duration-300",
          sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-[260px]",
          "ml-0",
          user?.role === 'staff' && "pb-24 lg:pb-0"
        )}
      >

        {/* Header */}
        <header className="sticky top-0 z-[55] backdrop-blur-xl bg-background/90 border-b border-white/[0.06]">
          <div className="flex items-center justify-between h-16 sm:h-16 px-3 sm:px-4 lg:px-8 py-3 sm:py-0">

            {/* LEFT */}
            <div className="flex items-center gap-3 min-w-0">

              {/* Mobile Menu */}
              <button
                onClick={() => document.body.classList.toggle('mobile-sidebar-open')}
                className="lg:hidden w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center border border-white/10 text-white/70 hover:text-white hover:bg-white/[0.04] transition-all flex-shrink-0"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Persistent page name — small + muted; stays pinned on scroll so
                  users always know where they are (the big in-page title scrolls away). */}
              {pageTitles[location.pathname]
                && !location.pathname.startsWith('/vendor/')
                && !location.pathname.startsWith('/planner/') && (
                <span className="text-sm font-medium text-white/55 truncate min-w-0" data-testid="topbar-page-name">{title}</span>
              )}
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-2 sm:gap-3">

              {/* Role Badge for team members - desktop only */}
              {user?.role === 'admin' && user?.role !== 'manager' && (
                <span className="hidden lg:inline-flex px-3 py-1.5 text-[11px] font-medium rounded-full whitespace-nowrap tracking-wider uppercase" style={{ background: 'rgba(139,92,246,0.08)', color: 'rgba(167,139,250,0.8)', border: '1px solid rgba(139,92,246,0.12)' }} data-testid="role-badge-admin">
                  Admin Access
                </span>
              )}
              {user?.role === 'member' && (
                <span className="hidden lg:inline-flex px-3 py-1.5 text-[11px] font-medium rounded-full whitespace-nowrap tracking-wider uppercase" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="role-badge-member">
                  Team Member
                </span>
              )}

              {/* Role Switcher - hidden on small mobile */}
              <div className="hidden sm:block">
                <RoleSwitcher user={user} />
              </div>

              {/* Industry badge removed — retired the per-industry PNG icons */}

              {/* BLOCK S7 2 — ONE "? Help" button replaces the walkthrough
                  chip. StaffedProCertificate stays mounted below for the
                  certificate modal + award logic (chip removed). */}
              <HelpMenu />
              <StaffedProCertificate />

              {/* Command palette launcher (⌘K / Ctrl+K) */}
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('staffed:command-palette'))}
                className="inline-flex items-center gap-2 h-9 px-2.5 sm:px-3 rounded-xl border border-white/10 bg-white/[0.03] text-white/45 hover:text-white/75 hover:bg-white/[0.06] transition-colors"
                data-testid="command-palette-launcher"
                title="Jump to any page (⌘K)"
                aria-label="Open command palette"
              >
                <Search className="w-4 h-4" />
                <span className="hidden md:inline text-[12px]">Jump to…</span>
                <kbd className="hidden md:inline text-[10px] text-white/40 border border-white/15 rounded px-1.5 py-0.5">⌘K</kbd>
              </button>
              {/* Role Preview Switcher (admin-only, hidden for staff/vendor) */}
              <RolePreviewSwitcher realUser={realUser || user} />

              {/* Notification Button */}
              <button
                onClick={() => { setShowNotifications(!showNotifications); setShowProfileMenu(false); }}
                className={`relative w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center border border-white/10 bg-white/[0.03] transition-all transition-transform duration-200 active:scale-95 ${ratingFlash && user?.role !== 'staff' && user?.role !== 'vendor' ? 'animate-pulse' : ''} ${bellLuminate ? 'mtl-luminate' : ''} ${leadUnseen > 0 ? 'mtl-bell-pulse' : ''}`}
                style={{
                  background: showNotifications ? 'rgba(212,175,55,0.08)' : (pendingRatingsCount > 0 && user?.role !== 'staff' && user?.role !== 'vendor') ? 'rgba(212,175,55,0.06)' : undefined,
                  borderColor: showNotifications ? 'rgba(212,175,55,0.2)' : (pendingRatingsCount > 0 && user?.role !== 'staff' && user?.role !== 'vendor') ? 'rgba(212,175,55,0.25)' : undefined,
                }}
                data-testid="notifications-btn"
                data-luminate={bellLuminate ? 'true' : 'false'}
              >
                <Bell className="w-5 h-5 text-white/70" />
                {/* For staff/vendor, only show unreadCount; for admin/manager, include pendingRatingsCount */}
                {(() => {
                  const isStaffOrVendor = user?.role === 'staff' || user?.role === 'vendor';
                  const displayCount = isStaffOrVendor ? bellUnseen : (bellUnseen + pendingRatingsCount);
                  return displayCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                      {displayCount > 9 ? '9+' : displayCount}
                    </span>
                  );
                })()}
              </button>

              {/* Rating Flash Flyout - Hidden for staff/vendor users
                  ── Positioning note (Feb 2026) ──
                  Was `top-14 sm:top-16` (right below the top nav) which put
                  this floating pill on top of PageHeader's built-in "Show me"
                  button at the same right anchor — they visibly overlapped
                  whenever a rating was pending. Now offset to `top-36 sm:top-40`
                  so it lives in the SAME right-aligned vertical column as
                  the Show me button but sits *below* it with clear breathing
                  room (Show me is the persistent action; this pill is a
                  temporary 8-second nudge). No layout jump on Show me — it
                  stays exactly where it always was; only the flyout moved. */}
              {ratingFlash && pendingRatingsCount > 0 && !showNotifications && user?.role !== 'staff' && user?.role !== 'vendor' && (
                <div 
                  className="absolute top-36 sm:top-40 right-3 sm:right-4 lg:right-8 animate-in slide-in-from-top-2 fade-in duration-300 cursor-pointer z-[9999]"
                  onClick={() => { setRatingFlash(false); onShowRatings?.(); }}
                  data-testid="rating-flash-flyout"
                >
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-xl whitespace-nowrap" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))', border: '1px solid rgba(212,175,55,0.3)', backdropFilter: 'blur(12px)' }}>
                    <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-sm font-medium text-[#D4AF37]">Rate Staff ({pendingRatingsCount})</span>
                  </div>
                </div>
              )}

              {/* Profile */}
              <div className="relative" ref={profileMenuRef}>
                <button 
                  onClick={() => { setShowProfileMenu(!showProfileMenu); setShowNotifications(false); }}
                  className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl overflow-hidden border border-white/10 flex items-center justify-center flex-shrink-0 transition-all hover:opacity-80"
                  style={{
                    background: companyLogo
                      ? (logoBackground === 'light' ? '#FFFFFF' : '#0B0F1A')
                      : 'linear-gradient(135deg, #D4AF37, #E5C76B)',
                  }}
                  data-testid="company-logo-btn"
                >
                  {companyLogo ? (
                    <img 
                      src={companyLogo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${companyLogo}` : companyLogo} 
                      alt="Company Logo" 
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <span className="text-sm font-bold text-white">
                      {user?.firstName?.[0] || 'U'}
                    </span>
                  )}
                </button>

                {/* Profile Dropdown — closes on outside click via profileMenuRef */}
                {showProfileMenu && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-[#0B0F1A] border border-white/10 rounded-xl py-2 z-[9999] shadow-2xl shadow-black/70" data-testid="profile-dropdown">
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                      <p className="text-sm font-medium text-white truncate">{user?.companyName || user?.firstName || 'My Account'}</p>
                      <p className="text-xs text-white/40 truncate mt-0.5">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      {/* Contact Office — staff & team members only (owners ARE the office). */}
                      {(user?.role === 'staff' || user?.isTeamMember) && (officePhone || officeEmail) && (
                        <>
                          <div className="px-4 pt-1.5 pb-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/60 font-semibold">Contact Office</p>
                          </div>
                          {officePhone && (
                            <a
                              href={`tel:${String(officePhone).replace(/[^\d+]/g, '')}`}
                              onClick={() => setShowProfileMenu(false)}
                              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                              data-testid="contact-office-call"
                            >
                              <Phone className="w-4 h-4 text-[#D4AF37]" />
                              <span>Call Office</span>
                            </a>
                          )}
                          {officeEmail && (
                            <a
                              href={`mailto:${officeEmail}`}
                              onClick={() => setShowProfileMenu(false)}
                              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                              data-testid="contact-office-email"
                            >
                              <Mail className="w-4 h-4 text-[#D4AF37]" />
                              <span>Email Office</span>
                            </a>
                          )}
                          <div className="my-1 border-t border-white/[0.06]" />
                        </>
                      )}
                      <a
                        href={user?.role === 'staff' ? '/staff/help' : '/account/features'}
                        onClick={(e) => { e.preventDefault(); setShowProfileMenu(false); navigate(user?.role === 'staff' ? '/staff/help' : '/account/features'); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                        data-testid="help-center-dropdown-link"
                      >
                        <HelpCircle className="w-4 h-4" />
                        <span>Help Center</span>
                      </a>
                      <button
                        onClick={() => { setShowProfileMenu(false); onLogout(); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        data-testid="profile-sign-out-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* MOBILE NOTIFICATIONS (FULL SCREEN SHEET) */}
        {showNotifications && (
          <div className="fixed inset-0 z-[9999] lg:hidden animate-backdrop" onClick={() => setShowNotifications(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" style={notifDragY ? { opacity: Math.max(0, 1 - notifDragY / 450) } : undefined} />
            <div
              className={`absolute inset-0 h-full bg-[#0B0F1A] rounded-t-2xl border-t border-white/10 flex flex-col ${notifInteracted ? '' : 'animate-sheet-in'}`}
              style={{ transform: notifDragY ? `translateY(${notifDragY}px)` : undefined, transition: notifSheetDragging ? 'none' : 'transform 280ms cubic-bezier(.22,1,.36,1)' }}
              onClick={(e) => e.stopPropagation()}
            >

              {/* Handle */}
              <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing" style={{ touchAction: 'none' }} onPointerDown={(e) => beginNotifDrag(e, 'handle')}>
                <div className="w-10 h-1 bg-white/20 rounded-full" />
              </div>

              {notifView === 'preferences' ? (
                <>
                  {/* Preferences Header */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                    <button onClick={() => setNotifView('list')} className="p-1 hover:bg-white/10 rounded-lg">
                      <ArrowLeft className="w-5 h-5 text-gray-400" />
                    </button>
                    <h3 className="text-white text-base font-medium">Notification Preferences</h3>
                  </div>
                  {/* Preferences Body */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${prefs?.muteAll ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                          {prefs?.muteAll ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">Mute All</p>
                          <p className="text-xs text-gray-500">Pause all notifications</p>
                        </div>
                      </div>
                      <MiniToggle checked={prefs?.muteAll || false} onChange={handleToggleMuteAll} disabled={savingPrefs} />
                    </div>
                    {(user?.role !== 'staff' && user?.role !== 'vendor' && user?.role !== 'member') && (
                      <div className={`flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 ${prefs?.muteAll ? 'opacity-40' : ''}`} data-testid="lead-sound-row-mobile">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#D4AF37]/15">
                            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">New Lead Sound</p>
                            <p className="text-xs text-gray-500">Play a chime when a new lead arrives</p>
                          </div>
                        </div>
                        <MiniToggle checked={prefs?.leadSound !== false} onChange={handleToggleLeadSound} disabled={savingPrefs || prefs?.muteAll} />
                      </div>
                    )}
                    {user?.role === 'staff' && (
                      <div className={`flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 ${prefs?.muteAll ? 'opacity-40' : ''}`} data-testid="assignment-sound-row-mobile">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#D4AF37]/15">
                            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">New Assignment Sound</p>
                            <p className="text-xs text-gray-500">Play a chime when you're added to an event</p>
                          </div>
                        </div>
                        <MiniToggle checked={prefs?.assignmentSound !== false} onChange={handleToggleAssignmentSound} disabled={savingPrefs || prefs?.muteAll} />
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {PREF_CATEGORIES.map(({ key, icon: CatIcon, label }) => {
                        const enabled = prefs?.categories?.[key] ?? true;
                        return (
                          <div key={key} className={`flex items-center justify-between py-3 px-3 rounded-lg transition-colors ${prefs?.muteAll ? 'opacity-40' : 'hover:bg-white/5'}`}>
                            <div className="flex items-center gap-3">
                              <CatIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-200">{label}</span>
                            </div>
                            <MiniToggle checked={enabled} onChange={(v) => handleToggleCategory(key, v)} disabled={savingPrefs || prefs?.muteAll} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-white/10" style={{ touchAction: 'pan-y' }} onPointerDown={(e) => beginNotifDrag(e, 'header')}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-white text-base font-medium">Notifications</h3>
                      <div className="flex items-center gap-3">
                        {!clearAllConfirm && (
                          <>
                            {unreadCount > 0 && (
                              <button onClick={markAllRead} className="text-xs text-[#D4AF37] hover:text-[#E5C76B] transition-colors">
                                Mark all read
                              </button>
                            )}
                            {notifications.length > 0 && (
                              <button
                                onClick={() => setClearAllConfirm(true)}
                                className="text-xs text-[#F87171]/80 hover:text-[#F87171] transition-colors"
                                data-testid="clear-all-notifications-btn"
                              >
                                Clear all
                              </button>
                            )}
                          </>
                        )}
                        <button onClick={() => setNotifView('preferences')} className="p-1 hover:bg-white/10 rounded-lg" data-testid="notification-settings-btn-mobile">
                          <Settings2 className="w-4 h-4 text-gray-400" />
                        </button>
                        <button onClick={() => setShowNotifications(false)}>
                          <X className="w-5 h-5 text-white/60" />
                        </button>
                      </div>
                    </div>
                    {/* Full-width row below the title: the ~185px header group
                        wrapped this message onto five lines and collided with
                        the title. */}
                    {clearAllConfirm && (
                      <div
                        className="mt-2 w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                        style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.22)' }}
                        data-testid="clear-all-confirm-bar"
                      >
                        <span className="text-[11px] leading-snug text-white/75">Clear all notifications? They'll be removed from your bell.</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            onClick={() => setClearAllConfirm(false)}
                            className="text-[11px] text-white/60 hover:text-white/90 transition-colors"
                            data-testid="clear-all-cancel"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={clearAll}
                            disabled={clearingAll}
                            className="text-[11px] font-medium text-[#F87171] hover:text-[#FCA5A5] transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="clear-all-confirm"
                          >
                            {clearingAll && <RefreshCw className="w-3 h-3 animate-spin" />}
                            Clear all
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Filter tabs */}
                    <div className="flex gap-1">
                      {['all', 'unread'].map(f => (
                        <button
                          key={f}
                          onClick={() => setNotifFilter(f)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                            notifFilter === f
                              ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/25'
                              : 'text-gray-400 hover:bg-white/5 border border-transparent'
                          }`}
                          data-testid={`notif-filter-${f}`}
                        >
                          {f === 'all' ? 'All' : `Unread (${unreadCount})`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* List */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }} onPointerDown={(e) => beginNotifDrag(e, 'list', e.currentTarget)}>
                    {/* Pending Ratings - Pinned at top (admin only) */}
                    {pendingRatingsCount > 0 && user?.role !== 'staff' && user?.role !== 'vendor' && (
                      <div 
                        onClick={() => { setShowNotifications(false); onShowRatings?.(); }}
                        className="px-4 py-4 border-b border-[#D4AF37]/20 cursor-pointer"
                        style={{ background: 'rgba(212,175,55,0.06)' }}
                        data-testid="rate-staff-notification-mobile"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#E5C76B] flex items-center justify-center flex-shrink-0">
                            <Star className="w-4 h-4 text-[#0B0F1A] fill-[#0B0F1A]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#D4AF37]">Rate Staff</p>
                            <p className="text-xs text-gray-400">{pendingRatingsCount} staff member{pendingRatingsCount !== 1 ? 's' : ''} awaiting your rating</p>
                          </div>
                          <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse flex-shrink-0" />
                        </div>
                      </div>
                    )}

                    {loadingNotifications ? (
                      <div className="py-8 text-center">
                        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : (() => {
                      const filtered = notifFilter === 'unread' ? notifications.filter(n => !n.read) : notifications;
                      const feed = buildGroupedFeed(filtered);
                      return feed.length > 0 ? (
                      feed.map((entry) => {
                        const isReadOnly = false;
                        if (entry.kind === 'single') {
                        const notification = entry.notification;
                        return (
                        <div
                          key={notification.id}
                          onClick={isReadOnly ? undefined : () => handleNotificationClick(notification)}
                          className={`px-4 py-4 border-b border-white/5 select-none touch-manipulation ${isReadOnly ? 'cursor-default' : 'cursor-pointer active:bg-white/5'} ${!notification.read ? 'bg-[#D4AF37]/5' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">{getNotificationIcon(notification.type)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${!notification.read ? 'text-white' : 'text-gray-300'}`}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-white/50 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>
                        );
                        }
                        // ── Cascade group ──
                        const { cascadeId, members } = entry;
                        const isExpanded = !!expandedCascades[cascadeId];
                        const unreadInGroup = members.filter(m => !m.read).length;
                        const clientName = members[0]?.metadata?.clientName || 'New Client';
                        return (
                          <div key={cascadeId} className={`${unreadInGroup > 0 ? 'bg-[#D4AF37]/5' : ''}`} data-testid={`notification-cascade-${cascadeId}`}>
                            <div
                              onClick={() => toggleCascade(cascadeId)}
                              className="px-4 py-4 border-b border-white/5 cursor-pointer hover:bg-white/5"
                              data-testid={`cascade-header-${cascadeId}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37]/30 to-emerald-500/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                                  <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className={`text-sm font-semibold ${unreadInGroup > 0 ? 'text-white' : 'text-gray-300'}`}>
                                      New Booking — {clientName}
                                    </p>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {unreadInGroup > 0 && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">{unreadInGroup} new</span>
                                      )}
                                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                  </div>
                                  <p className="text-xs text-white/50 mt-1">
                                    {members.length} item{members.length === 1 ? '' : 's'} to review — event, client, location, budget.
                                  </p>
                                  {!isReadOnly && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleGroupPrimaryClick(entry); }}
                                      className="mt-2 text-xs text-[#D4AF37] hover:text-[#E5C76B] font-medium"
                                      data-testid={`cascade-review-all-${cascadeId}`}
                                    >
                                      Review all {members.length} →
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="bg-black/20 pl-6 border-l-2 border-[#D4AF37]/20 ml-6 py-1" data-testid={`cascade-members-${cascadeId}`}>
                                {members.map(m => (
                                  <div
                                    key={m.id}
                                    onClick={isReadOnly ? undefined : () => handleNotificationClick(m)}
                                    className={`px-3 py-2 border-b border-white/5 ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-white/5'}`}
                                    data-testid={`notification-${m.id}`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <div className="mt-0.5">{getNotificationIcon(m.type)}</div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-[13px] font-medium ${m.read ? 'text-gray-400' : 'text-white'}`}>
                                          {m.title}
                                        </p>
                                        <p className="text-[11px] text-white/40 mt-0.5 line-clamp-1">{m.message}</p>
                                      </div>
                                      {!m.read && <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full flex-shrink-0 mt-1.5" />}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Bell className="w-8 h-8 text-gray-600 mb-2" />
                        <p className="text-white/40 text-sm">{notifFilter === 'unread' ? 'No unread notifications' : 'No notifications'}</p>
                      </div>
                    );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* DESKTOP NOTIFICATIONS DROPDOWN */}
        {showNotifications && (
          <div className="hidden lg:block">
            <div className="fixed inset-0 z-[9998]" onClick={() => setShowNotifications(false)} />
            <div
              className="fixed top-16 right-8 w-[340px] rounded-2xl overflow-hidden z-[9999] animate-panel-in origin-top-right"
              style={{
                background: 'linear-gradient(180deg, rgba(15,18,30,0.98) 0%, rgba(10,12,22,0.99) 100%)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
              }}
              data-testid="notification-dropdown-desktop"
            >
              {notifView === 'preferences' ? (
                <>
                  {/* Preferences Header */}
                  <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <button onClick={() => setNotifView('list')} className="p-1 hover:bg-white/10 rounded-lg" data-testid="prefs-back-btn">
                      <ArrowLeft className="w-4 h-4 text-gray-400" />
                    </button>
                    <h3 className="text-white text-sm font-semibold">Notification Preferences</h3>
                  </div>
                  {/* Preferences Body */}
                  <div className="max-h-80 overflow-y-auto p-4 space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${prefs?.muteAll ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                          {prefs?.muteAll ? <VolumeX className="w-3.5 h-3.5 text-red-400" /> : <Volume2 className="w-3.5 h-3.5 text-emerald-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">Mute All</p>
                          <p className="text-[10px] text-gray-500">Pause all notifications</p>
                        </div>
                      </div>
                      <MiniToggle checked={prefs?.muteAll || false} onChange={handleToggleMuteAll} disabled={savingPrefs} />
                    </div>
                    {(user?.role !== 'staff' && user?.role !== 'vendor' && user?.role !== 'member') && (
                      <div className={`flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 ${prefs?.muteAll ? 'opacity-40' : ''}`} data-testid="lead-sound-row-desktop">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#D4AF37]/15">
                            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">New Lead Sound</p>
                            <p className="text-[10px] text-gray-500">Play a chime when a new lead arrives</p>
                          </div>
                        </div>
                        <MiniToggle checked={prefs?.leadSound !== false} onChange={handleToggleLeadSound} disabled={savingPrefs || prefs?.muteAll} />
                      </div>
                    )}
                    {user?.role === 'staff' && (
                      <div className={`flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 ${prefs?.muteAll ? 'opacity-40' : ''}`} data-testid="assignment-sound-row-desktop">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#D4AF37]/15">
                            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">New Assignment Sound</p>
                            <p className="text-[10px] text-gray-500">Play a chime when you're added to an event</p>
                          </div>
                        </div>
                        <MiniToggle checked={prefs?.assignmentSound !== false} onChange={handleToggleAssignmentSound} disabled={savingPrefs || prefs?.muteAll} />
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {PREF_CATEGORIES.map(({ key, icon: CatIcon, label }) => {
                        const enabled = prefs?.categories?.[key] ?? true;
                        return (
                          <div key={key} className={`flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors ${prefs?.muteAll ? 'opacity-40' : 'hover:bg-white/5'}`}>
                            <div className="flex items-center gap-3">
                              <CatIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-200">{label}</span>
                            </div>
                            <MiniToggle checked={enabled} onChange={(v) => handleToggleCategory(key, v)} disabled={savingPrefs || prefs?.muteAll} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Header */}
                  <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-white/90 tracking-wide">Notifications</h3>
                        {unreadCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/20">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!clearAllConfirm && (
                          <>
                            {unreadCount > 0 && (
                              <button
                                onClick={markAllRead}
                                className="text-[11px] text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors font-medium"
                                data-testid="mark-all-read-btn"
                              >
                                Mark all read
                              </button>
                            )}
                            {notifications.length > 0 && (
                              <button
                                onClick={() => setClearAllConfirm(true)}
                                className="text-[11px] text-[#F87171]/70 hover:text-[#F87171] transition-colors font-medium"
                                data-testid="clear-all-notifications-btn"
                              >
                                Clear all
                              </button>
                            )}
                          </>
                        )}
                        <button onClick={() => setNotifView('preferences')} className="p-1 hover:bg-white/10 rounded-lg" data-testid="notification-settings-btn">
                          <Settings2 className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                      </div>
                    </div>
                    {/* Full-width row below the title: the ~185px header group
                        wrapped this message onto five lines and collided with
                        the title. */}
                    {clearAllConfirm && (
                      <div
                        className="mt-2 w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                        style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.22)' }}
                        data-testid="clear-all-confirm-bar"
                      >
                        <span className="text-[11px] leading-snug text-white/75">Clear all notifications? They'll be removed from your bell.</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            onClick={() => setClearAllConfirm(false)}
                            className="text-[11px] text-white/60 hover:text-white/90 transition-colors font-medium"
                            data-testid="clear-all-cancel"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={clearAll}
                            disabled={clearingAll}
                            className="text-[11px] font-medium text-[#F87171] hover:text-[#FCA5A5] transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="clear-all-confirm"
                          >
                            {clearingAll && <RefreshCw className="w-3 h-3 animate-spin" />}
                            Clear all
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-[380px] overflow-y-auto">
                    {/* Pending Ratings Pinned */}
                    {pendingRatingsCount > 0 && user?.role !== 'staff' && user?.role !== 'vendor' && (
                      <div
                        onClick={() => { setShowNotifications(false); onShowRatings?.(); }}
                        className="px-4 py-3 cursor-pointer transition-colors hover:bg-[#D4AF37]/[0.06]"
                        style={{ background: 'rgba(212,175,55,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        data-testid="rate-staff-notification"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#E5C76B] flex items-center justify-center flex-shrink-0">
                            <Star className="w-3.5 h-3.5 text-[#0B0F1A] fill-[#0B0F1A]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-[#D4AF37]">Rate Staff</p>
                            <p className="text-[11px] text-white/30">{pendingRatingsCount} staff awaiting your rating</p>
                          </div>
                          <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse flex-shrink-0" />
                        </div>
                      </div>
                    )}

                    {loadingNotifications ? (
                      <div className="py-10 text-center">
                        <div className="w-5 h-5 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mx-auto" />
                      </div>
                    ) : (() => {
                      const filtered = notifFilter === 'unread' ? notifications.filter(n => !n.read) : notifications;
                      const feed = buildGroupedFeed(filtered);
                      return feed.length > 0 ? (
                        <div>
                          {feed.map((entry, idx) => {
                            const isReadOnly = false;
                            if (entry.kind === 'single') {
                            const notification = entry.notification;
                            const { Icon, color, bg } = getNotifStyle(notification);
                            const isUnread = !notification.read;
                            return (
                              <div
                                key={notification.id}
                                onClick={isReadOnly ? undefined : () => handleNotificationClick(notification)}
                                className={`group relative px-4 py-3 transition-all duration-150 ${
                                  isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-white/[0.04]'
                                } ${isUnread ? 'bg-white/[0.025]' : ''}`}
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                                data-testid={`notification-${notification.id}`}
                              >
                                {isUnread && (
                                  <div className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r-full bg-[#D4AF37]" />
                                )}
                                <div className="flex items-start gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-[13px] font-medium leading-snug ${isUnread ? 'text-white/90' : 'text-white/50'}`}>
                                      {notification.title}
                                    </p>
                                    <p className={`text-[12px] mt-0.5 leading-relaxed line-clamp-2 ${isUnread ? 'text-white/40' : 'text-white/25'}`}>
                                      {notification.message}
                                    </p>
                                    <p className={`text-[11px] mt-1.5 ${isUnread ? 'text-white/25' : 'text-white/15'}`}>
                                      {notification.time || (notification.createdAt && (() => {
                                        const d = new Date(notification.createdAt);
                                        const now = new Date();
                                        const diff = Math.floor((now - d) / 60000);
                                        if (diff < 1) return 'Just now';
                                        if (diff < 60) return `${diff}m ago`;
                                        const h = Math.floor(diff / 60);
                                        if (h < 24) return `${h}h ago`;
                                        const days = Math.floor(h / 24);
                                        if (days < 7) return `${days}d ago`;
                                        return d.toLocaleDateString();
                                      })())}
                                    </p>
                                  </div>
                                  {isUnread && (
                                    <span className="w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0 mt-1.5" />
                                  )}
                                </div>
                              </div>
                            );
                            }
                            // ── Cascade group ──
                            const { cascadeId, members } = entry;
                            const isExpanded = !!expandedCascades[cascadeId];
                            const unreadInGroup = members.filter(m => !m.read).length;
                            const clientName = members[0]?.metadata?.clientName || 'New Client';
                            return (
                              <div key={cascadeId} className={`${unreadInGroup > 0 ? 'bg-white/[0.025]' : ''}`} data-testid={`notification-cascade-${cascadeId}`}>
                                <div
                                  onClick={() => toggleCascade(cascadeId)}
                                  className="group relative px-4 py-3 cursor-pointer hover:bg-white/[0.04] transition-all"
                                  style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                                  data-testid={`cascade-header-${cascadeId}`}
                                >
                                  {unreadInGroup > 0 && (
                                    <div className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r-full bg-[#D4AF37]" />
                                  )}
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#D4AF37]/30 to-emerald-500/20 border border-[#D4AF37]/40">
                                      <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <p className={`text-[13px] font-semibold leading-snug ${unreadInGroup > 0 ? 'text-white/95' : 'text-white/50'}`}>
                                          New Booking — {clientName}
                                        </p>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          {unreadInGroup > 0 && (
                                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">{unreadInGroup} new</span>
                                          )}
                                          <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                        </div>
                                      </div>
                                      <p className={`text-[12px] mt-0.5 leading-relaxed ${unreadInGroup > 0 ? 'text-white/45' : 'text-white/25'}`}>
                                        {members.length} item{members.length === 1 ? '' : 's'} ready to review — event, client, location, and budget.
                                      </p>
                                      {!isReadOnly && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleGroupPrimaryClick(entry); }}
                                          className="mt-1.5 text-[11px] text-[#D4AF37] hover:text-[#E5C76B] font-semibold"
                                          data-testid={`cascade-review-all-${cascadeId}`}
                                        >
                                          Review all {members.length} →
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div className="bg-black/20 border-l-2 border-[#D4AF37]/20 ml-6" data-testid={`cascade-members-${cascadeId}`}>
                                    {members.map(m => {
                                      const { Icon: MIcon, color: mColor, bg: mBg } = getNotifStyle(m);
                                      return (
                                        <div
                                          key={m.id}
                                          onClick={isReadOnly ? undefined : () => handleNotificationClick(m)}
                                          className={`px-4 py-2.5 ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-white/[0.04]'}`}
                                          style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                                          data-testid={`notification-${m.id}`}
                                        >
                                          <div className="flex items-start gap-2.5">
                                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${mBg}`}>
                                              <MIcon className={`w-3 h-3 ${mColor}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className={`text-[12px] font-medium ${m.read ? 'text-white/50' : 'text-white/90'}`}>
                                                {m.title}
                                              </p>
                                              <p className="text-[11px] text-white/35 mt-0.5 line-clamp-1">{m.message}</p>
                                            </div>
                                            {!m.read && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0 mt-1.5" />}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-12 text-center">
                          <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center mx-auto mb-3">
                            <Bell className="w-5 h-5 text-white/15" />
                          </div>
                          <p className="text-sm text-white/30">{notifFilter === 'unread' ? 'No unread updates' : 'No new updates'}</p>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Pass-through fee reminder banner — company/planner admins only,
            while the tenant is still absorbing the Stripe fee. */}
        {user?.role !== 'staff' && user?.role !== 'vendor' && user?.role !== 'member'
          && feePassThrough === false && !feeBannerDismissed && (
          <div className="mx-3 sm:mx-4 lg:mx-8 mt-4 flex items-center gap-3 rounded-xl border border-[#D4AF37]/30 bg-[linear-gradient(90deg,rgba(212,175,55,0.12),rgba(212,175,55,0.03))] px-4 py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
            <DollarSign className="w-4 h-4 text-[#D4AF37] shrink-0" />
            <p className="text-[13px] leading-5 text-white/80 flex-1 min-w-0">
              <span className="font-semibold text-[#E3C45B]">Heads up —</span> you're absorbing the Stripe processing fee on every payment. Turn on pass-through to charge it to clients instead.
            </p>
            <button
              onClick={enablePassThroughInline}
              disabled={enablingFee}
              className="shrink-0 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] text-[12px] font-semibold px-3.5 py-1.5 hover:brightness-110 transition disabled:opacity-60"
              data-testid="fee-banner-cta"
            >
              {enablingFee ? 'Turning on…' : 'Turn it on'}
            </button>
            <button
              onClick={() => { setFeeBannerDismissed(true); appSession.setItem('feeBannerDismissed', '1'); }}
              className="shrink-0 text-white/40 hover:text-white transition"
              title="Dismiss"
              data-testid="fee-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page Content — path-keyed .lux-page-fade gives every route a
            gentle 0.25s crossfade on navigation (reduced-motion guarded in
            index.css). Keying by pathname replays the fade on each route.
            Staff on the bottom-nav routes get a direction-aware
            .staff-page-enter instead — a 220ms slide+fade that matches the
            drawer's briefFade feel and eliminates the between-page flash. */}
        <main className="px-3 sm:px-4 lg:px-8 py-4 sm:py-6 overscroll-contain" style={user?.role === 'staff' ? { background: '#080A14' } : undefined}>
          {(() => {
            const isStaff = user?.role === 'staff';
            // S58 — the Command Deck must NOT get this shared page-level fade
            // wrapper. It carries lux-page-fade (0.25s), transition:all and
            // will-change:opacity, and released the whole Deck as one faded
            // slab, fighting the per-section deck-enter entrances. Every OTHER
            // route keeps the crossfade, so the class/keyframe stay defined.
            const isCommandDeck = location.pathname === '/control-center';
            let dir = 'left';
            if (isStaff) {
              try { dir = appSession.getItem('staffNavDir') || 'left'; } catch (_) { /* appSession disabled */ }
            }
            const enterX = dir === 'left' ? '14px' : '-14px';
            return (
              <div
                key={location.pathname.startsWith('/settings') ? '/settings' : location.pathname}
                className={isStaff ? 'staff-page-enter' : (isCommandDeck ? undefined : 'lux-page-fade')}
                style={isStaff ? { ['--enter-x']: enterX } : undefined}
              >
                {children}
              </div>
            );
          })()}
        </main>
      </div>

      {/* Push Notification Prompt */}
      <NotificationPrompt />
      <BlockoutAlertBanner />
      <StaffDeclineAlertBanner />
      <NeedsAttentionStack user={user} />
      <StaffBottomNav user={user} />
    </div>
  );
};

export default DashboardLayout;
